import { FormEvent, useEffect, useId, useMemo, useState } from 'react';

import { Button, Input, Modal, Select } from '../ui/primitives';
import { useChargeTakeawayOrder } from '../../hooks/useChargeTakeawayOrder';
import { formatCurrency } from '../../utils/currency';
import { ReceiptRenderer } from '../Receipt/ReceiptRenderer';
import { buildReceiptData, type ReceiptData } from '../../utils/receipt';
import { getProducts } from '../../services/ProductService';
import { getCashSession } from '../../services/CashService';
import { useQuery } from '@tanstack/react-query';
import { safeArray } from '../../utils/safe';

interface TakeawayChargeModalProps {
  orderId: string | number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialAmount: number;
}

import { getPaymentMethods } from '../../services/paymentMethodService';
// ... (imports)

// remove ChargeMethod type definition if unused or replace with string

export function TakeawayChargeModal({
  orderId,
  open,
  onClose,
  onSuccess,
  initialAmount,
}: TakeawayChargeModalProps) {
  const titleId = useId();
  // method is now string
  const [method, setMethod] = useState<string>('');
  const [amount, setAmount] = useState('');
  const [tip, setTip] = useState('');
  const [received, setReceived] = useState('');
  const [reference, setReference] = useState('');
  const [cashRegisterId, setCashRegisterId] = useState<number | null>(null);
  const chargeMutation = useChargeTakeawayOrder();
  const { reset } = chargeMutation;

  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: getProducts,
  });

  const { data: paymentMethods } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: getPaymentMethods,
    enabled: open
  });

  const [receiptToPrint, setReceiptToPrint] = useState<ReceiptData | null>(null);

  // Set default method
  useEffect(() => {
    if (open && paymentMethods && paymentMethods.length > 0 && !method) {
      const cash = paymentMethods.find(m => m.type === 'CASH');
      setMethod(cash ? cash.name : paymentMethods[0].name);
    }
  }, [open, paymentMethods, method]);

  // Fetch active cash session when modal opens
  useEffect(() => {
    if (!open) return;
    getCashSession()
      .then((sess) => { if (sess?.id) setCashRegisterId(Number(sess.id)); })
      .catch(() => setCashRegisterId(null));
    setAmount(String(initialAmount ?? 0));
    setTip('');
    setReceived(String(initialAmount ?? 0));
    setReference('');
    reset();
  }, [open, initialAmount, reset]);

  const selectedMethodData = paymentMethods?.find(m => m.name === method);
  const isCash = selectedMethodData ? selectedMethodData.type === 'CASH' : false;

  const numericValues = useMemo(() => {
    const amountNumber = Number(amount);
    const tipNumber = tip ? Number(tip) : 0;
    const receivedNumber = received ? Number(received) : 0;
    return {
      amount: Number.isFinite(amountNumber) ? amountNumber : NaN,
      tip: Number.isFinite(tipNumber) ? tipNumber : 0,
      received: Number.isFinite(receivedNumber) ? receivedNumber : NaN,
    };
  }, [amount, tip, received]);

  const change = useMemo(() => {
    if (!isCash) return 0;
    if (!Number.isFinite(numericValues.amount)) return 0;
    if (!Number.isFinite(numericValues.received)) return 0;
    const diff = numericValues.received - (numericValues.amount + numericValues.tip);
    return diff > 0 ? diff : 0;
  }, [isCash, numericValues]);

  const canSubmit =
    method &&
    Number.isFinite(numericValues.amount) &&
    numericValues.amount > 0 &&
    (!isCash || Number.isFinite(numericValues.received));

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    const payload = {
      method,
      amount: numericValues.amount,
      tip: numericValues.tip || undefined,
      received: isCash ? numericValues.received : undefined,
      reference: !isCash && reference.trim().length > 0 ? reference.trim() : undefined,
      closeOrder: true,
      ...(cashRegisterId ? { cashRegisterId } : {}),
    };

    try {
      const result = await chargeMutation.mutateAsync({ id: orderId, payload });
      const updatedOrder = (result as any)?.data ?? result;

      // Build receipt
      const items = updatedOrder.items || [];
      const getVal = (n?: number | null) => (typeof n === 'number' && Number.isFinite(n) ? n : 0);

      const receiptItems = items.map((item: any) => {
        const qty = getVal(item.quantity);
        let unitPrice = 0;
        let lineTotal = 0;

        if (typeof item.base_price === 'number' && Number.isFinite(item.base_price)) {
          unitPrice = item.base_price;
        } else if (typeof item.product !== 'string' && item.product?.price) {
          unitPrice = Number(item.product.price);
        } else if (products && products.length > 0) {
          const pAny = item.product as any;
          const pId = typeof item.product === 'string' ? item.product : (pAny?.id || pAny?.id);
          const found = products.find(p => p.id === pId || p.id === pId);
          if (found && found.price) {
            unitPrice = found.price;
          }
        }

        if (typeof item.line_subtotal === 'number' && Number.isFinite(item.line_subtotal) && item.line_subtotal > 0) {
          lineTotal = item.line_subtotal;
          if (unitPrice === 0 && qty > 0) unitPrice = lineTotal / qty;
        } else {
          lineTotal = qty * unitPrice;
        }

        return {
          name: typeof item.product === 'string'
            ? (products?.find(p => p.id === item.product || p.id === item.product)?.name || 'Producto')
            : (item.product as any)?.name || 'Producto',
          quantity: qty,
          unitPrice: unitPrice,
          total: lineTotal,
          notes: item.kitchenSummary
        };
      });

      const calculatedSubtotal = receiptItems.reduce((acc: number, item: any) => acc + item.total, 0);

      const data: ReceiptData = buildReceiptData({
        order: updatedOrder,
        items: receiptItems,
        totals: { subtotal: calculatedSubtotal, total: calculatedSubtotal },
        customerName: updatedOrder.customerName,
        received: isCash ? numericValues.received : numericValues.amount,
        change: change,
        paidAt: new Date().toISOString(),
      });

      setReceiptToPrint(data);

    } catch {
      // handled by mutation onError
    }
  };

  return (
    <Modal open={open} onClose={onClose} titleId={titleId} className="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="border-b border-border px-6 py-5">
          <h2 id={titleId} className="text-lg font-semibold text-text">
            Cobrar pedido
          </h2>
          <p className="mt-1 text-sm text-text-muted">
            Monto a cobrar: {formatCurrency(numericValues.amount || initialAmount)}
          </p>
        </div>
        <div className="space-y-4 px-6">
          <label className="flex flex-col gap-1 text-sm text-text">
            Método de pago
            <Select value={method} onChange={(event) => setMethod(event.target.value)}>
              <option value="" disabled>Seleccione...</option>
              {safeArray(paymentMethods).filter(m => m.isActive).map(m => (
                <option key={m.id} value={m.name}>{m.name}</option>
              ))}
            </Select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-text">
            Monto
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-text">
            Propina (opcional)
            <Input
              type="number"
              step="0.01"
              min="0"
              value={tip}
              onChange={(event) => setTip(event.target.value)}
            />
          </label>
          {isCash ? (
            <label className="flex flex-col gap-1 text-sm text-text">
              Monto recibido
              <Input
                type="number"
                step="0.01"
                min="0"
                value={received}
                onChange={(event) => setReceived(event.target.value)}
              />
            </label>
          ) : null}
          {!isCash ? (
            <label className="flex flex-col gap-1 text-sm text-text">
              Referencia (opcional)
              <Input value={reference} onChange={(event) => setReference(event.target.value)} />
            </label>
          ) : null}
          {isCash && Number.isFinite(change) ? (
            <p className="text-sm text-text-muted">Cambio: {formatCurrency(change)}</p>
          ) : null}
        </div>
        <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={chargeMutation.isPending}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" isLoading={chargeMutation.isPending} disabled={!canSubmit}>
            Confirmar cobro
          </Button>
        </div>
      </form>
      <ReceiptRenderer data={receiptToPrint} onComplete={() => {
        setReceiptToPrint(null);
        onSuccess?.();
        onClose();
      }} />
    </Modal>
  );
}
